import React, { useState } from "react";
import { useUser } from "../../contexts/UserContext";
import axiosClient from "../../axios";
import { useNavigate } from "react-router-dom";

const BecomeAffiliate = () => {
  const { user, loading: userLoading } = useUser();
  const [bankName, setBankName] = useState("");
  const [bankAccountName, setBankAccountName] = useState("");
  const [bankAccountNumber, setBankAccountNumber] = useState("");
  const [ifscCode, setIfscCode] = useState("");
  const [address, setAddress] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const validateForm = () => {
    const newErrors = {};

    if (!referralCode.trim()) {
      newErrors.referralCode = "Referral code is required";
    }

    if (!bankName.trim()) {
      newErrors.bankName = "Bank name is required";
    }

    if (!bankAccountName.trim()) {
      newErrors.bankAccountName = "Account holder name is required";
    }

    if (!bankAccountNumber.trim()) {
      newErrors.bankAccountNumber = "Account number is required";
    } else if (!/^\d{9,18}$/.test(bankAccountNumber)) {
      newErrors.bankAccountNumber = "Please enter a valid account number";
    }

    if (!ifscCode.trim()) {
      newErrors.ifscCode = "IFSC code is required";
    } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifscCode)) {
      newErrors.ifscCode = "Please enter a valid IFSC code";
    }

    if (!address.trim()) {
      newErrors.address = "Address is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {
      await axiosClient.post("/api/affiliates/register", {
        referral_code: referralCode,
        bank_name: bankName,
        bank_account_name: bankAccountName,
        bank_account_number: bankAccountNumber,
        ifsc_code: ifscCode,
        address,
      });

      navigate("/account/coupons", {
        state: { message: "Application submitted successfully!" },
      });
    } catch (error) {
      if (error.response) {
        alert(error.response.data.error || "Failed to submit application.");
      } else if (error.request) {
        alert("No response from server. Please check your connection.");
      } else {
        alert("Error applying for affiliate program.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (userLoading) {
    return (
      <div className="container py-5">
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning text-center">
          <h4>Authentication Required</h4>
          <p>Please log in to access the affiliate program.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-8 col-xl-6">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-primary text-white py-4">
              <div className="text-center">
                <i className="bi bi-person-plus display-5 d-block mb-3"></i>
                <h3 className="card-title mb-0">Become an Affiliate</h3>
                <p className="mb-0 opacity-75">
                  Join our affiliate program and start earning
                </p>
              </div>
            </div>

            <div className="card-body p-4">
              <form onSubmit={handleSubmit}>
                {/* Referral Code */}
                <div className="mb-4">
                  <label className="form-label fw-semibold">
                    Referral Code *
                  </label>
                  <input
                    type="text"
                    className={`form-control ${
                      errors.referralCode ? "is-invalid" : ""
                    }`}
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value)}
                    required
                    placeholder="Enter the referral code you received"
                  />
                  {errors.referralCode && (
                    <div className="invalid-feedback">
                      {errors.referralCode}
                    </div>
                  )}
                </div>

                <div className="row">
                  {/* Bank Name */}
                  <div className="col-md-6 mb-4">
                    <label className="form-label fw-semibold">
                      Bank Name *
                    </label>
                    <input
                      type="text"
                      className={`form-control ${
                        errors.bankName ? "is-invalid" : ""
                      }`}
                      value={bankName}
                      onChange={(e) => setBankName(e.target.value)}
                      placeholder="e.g., State Bank of India"
                    />
                    {errors.bankName && (
                      <div className="invalid-feedback">{errors.bankName}</div>
                    )}
                  </div>

                  {/* Account Holder Name */}
                  <div className="col-md-6 mb-4">
                    <label className="form-label fw-semibold">
                      Account Holder Name *
                    </label>
                    <input
                      type="text"
                      className={`form-control ${
                        errors.bankAccountName ? "is-invalid" : ""
                      }`}
                      value={bankAccountName}
                      onChange={(e) => setBankAccountName(e.target.value)}
                      placeholder="e.g., John Doe"
                    />
                    {errors.bankAccountName && (
                      <div className="invalid-feedback">
                        {errors.bankAccountName}
                      </div>
                    )}
                  </div>
                </div>

                <div className="row">
                  {/* Account Number */}
                  <div className="col-md-6 mb-4">
                    <label className="form-label fw-semibold">
                      Account Number *
                    </label>
                    <input
                      type="text"
                      className={`form-control ${
                        errors.bankAccountNumber ? "is-invalid" : ""
                      }`}
                      value={bankAccountNumber}
                      onChange={(e) => setBankAccountNumber(e.target.value)}
                      placeholder="e.g., 1234567890"
                    />
                    {errors.bankAccountNumber && (
                      <div className="invalid-feedback">
                        {errors.bankAccountNumber}
                      </div>
                    )}
                  </div>

                  {/* IFSC Code */}
                  <div className="col-md-6 mb-4">
                    <label className="form-label fw-semibold">
                      IFSC Code *
                    </label>
                    <input
                      type="text"
                      className={`form-control ${
                        errors.ifscCode ? "is-invalid" : ""
                      }`}
                      value={ifscCode}
                      onChange={(e) =>
                        setIfscCode(e.target.value.toUpperCase())
                      }
                      placeholder="e.g., SBIN0000000"
                    />
                    {errors.ifscCode && (
                      <div className="invalid-feedback">{errors.ifscCode}</div>
                    )}
                  </div>
                </div>

                {/* Address */}
                <div className="mb-4">
                  <label className="form-label fw-semibold">Address *</label>
                  <textarea
                    className={`form-control ${
                      errors.address ? "is-invalid" : ""
                    }`}
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    rows="3"
                    placeholder="Enter your complete address"
                  />
                  {errors.address && (
                    <div className="invalid-feedback">{errors.address}</div>
                  )}
                </div>

                {/* Submit Button */}
                <div className="text-center">
                  <button
                    type="submit"
                    className="btn btn-primary btn-lg px-5"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        ></span>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-send-check me-2"></i>
                        Submit Application
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Footer Note */}
            <div className="card-footer bg-light py-3">
              <div className="text-center">
                <small className="text-muted">
                  <i className="bi bi-info-circle me-1"></i>
                  All fields marked with * are required. Your information is
                  secure and encrypted.
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BecomeAffiliate;
